import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, Grid, Paper, Typography } from "@mui/material";
import BackButton from "../../common/back-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import {
  MFD_LEADS_ENDPOINT,
  MFD_REGISTERED_LEADS_ENDPOINT,
} from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

const fmt = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (v) => (v ? formatWithTzLabel(v) : "—");

const STATUS_CHIP_COLOR = {
  Open: "info",
  "In Progress": "warning",
  Converted: "success",
  "Not Interested": "default",
};

const PRODUCT_INTEREST_COLOR = {
  Invested: "success",
  Interested: "primary",
  Deferred: "default",
};

const INTEREST_EXTRA_FIELDS = {
  "New Investment": [
    { key: "investment_amount", label: "Investment Amount", format: fmt },
    { key: "fund_type",         label: "Fund Type" },
    { key: "investment_period", label: "Investment Period", format: (v) => `${v} years` },
    { key: "created_at",        label: "Submitted At",      format: fmtDate },
  ],
  "Goal Planning": [
    { key: "goal_name",       label: "Goal" },
    { key: "target_amount",   label: "Target Amount",   format: fmt,                       onlyIfValue: true },
    { key: "years",           label: "Timeline",        format: (v) => `${v} years`,       onlyIfValue: true },
    { key: "sip_needed",      label: "Monthly SIP",     format: fmt,                       onlyIfValue: true },
    { key: "lumpsum_needed",  label: "Lumpsum Today",   format: fmt,                       onlyIfValue: true },
    // Retirement Planning (reuses the Retirement Calculator flow, so its data
    // uses the calculator's field names instead of the generic goal fields above)
    { key: "current_age",        label: "Current Age",                                    onlyIfValue: true },
    { key: "retirement_age",     label: "Retirement Age",                                 onlyIfValue: true },
    { key: "monthly_expenses",   label: "Monthly Expenses",   format: fmt,                 onlyIfValue: true },
    { key: "existing_corpus",    label: "Existing Corpus",    format: fmt,                 onlyIfValue: true },
    { key: "annual_roi",         label: "Annual ROI (%)",                                  onlyIfValue: true },
    { key: "years_to_retirement", label: "Years to Retirement",                            onlyIfValue: true },
    { key: "future_monthly_exp", label: "Inflation-Adjusted Monthly Expenses", format: fmt, onlyIfValue: true },
    { key: "future_existing",    label: "Future Value of Existing Corpus", format: fmt,    onlyIfValue: true },
    { key: "additional_needed",  label: "Additional Corpus Needed", format: fmt,           onlyIfValue: true },
    { key: "required_corpus",    label: "Required Corpus",    format: fmt,                 onlyIfValue: true },
    { key: "monthly_sip_needed", label: "Monthly SIP Needed", format: fmt,                 onlyIfValue: true },
    { key: "created_at",      label: "Submitted At",    format: fmtDate },
  ],
  "New Product Offering": [
    { key: "product_category",        label: "Product Category" },
    { key: "historical_returns",      label: "Historical Returns" },
    { key: "product_interest_status", label: "Interest Status", isProductStatus: true },
    { key: "created_at",              label: "Submitted At",    format: fmtDate },
  ],
  "Wealth Projection": [
    { key: "current_value",    label: "Current Portfolio Value", format: fmt },
    { key: "monthly_sip",      label: "Additional Monthly SIP",  format: fmt },
    { key: "annual_rate",      label: "Expected Return (%)" },
    { key: "years",            label: "Time Period",             format: (v) => `${v} years` },
    { key: "projected_wealth", label: "Projected Wealth",        format: fmt },
  ],
  "Review My Investments": [
    { key: "investment_value", label: "Investment Value" },
  ],
  "Product Offerings": [
    { key: "investment_value", label: "Investment Value" },
  ],
  "One-Time Investment": [
    { key: "investment_value", label: "Investment Value" },
    // SIF Bazaar Lumpsum Calculator
    { key: "annual_rate",      label: "Rate of Return (%)",  onlyIfValue: true },
    { key: "years",            label: "Duration",     format: (v) => `${v} years`, onlyIfValue: true },
    { key: "projected_corpus", label: "Future Value", format: fmt, onlyIfValue: true },
  ],
  "New SIP": [
    { key: "investment_value", label: "Investment Value" },
  ],
  "Meeting / Conversation": [
    { key: "investment_value", label: "Investment Value" },
  ],
  "Financial Calculators": [
    { key: "calculator_type",    label: "Calculator Type" },
    { key: "monthly_amount",     label: "Monthly Amount",     format: fmt,               onlyIfValue: true },
    { key: "annual_rate",        label: "Annual Rate (%)",                               onlyIfValue: true },
    { key: "years",              label: "Years",                                         onlyIfValue: true },
    { key: "invested",           label: "Total Invested",     format: fmt,               onlyIfValue: true },
    { key: "returns",            label: "Estimated Returns",  format: fmt,               onlyIfValue: true },
    { key: "projected_corpus",   label: "Projected Corpus",   format: fmt,               onlyIfValue: true },
    { key: "principal",          label: "Principal",          format: fmt,               onlyIfValue: true },
    { key: "monthly_withdrawal", label: "Monthly Withdrawal", format: fmt,               onlyIfValue: true },
    { key: "total_withdrawn",    label: "Total Withdrawn",    format: fmt,               onlyIfValue: true },
    { key: "remaining_corpus",   label: "Remaining Corpus",   format: fmt,               onlyIfValue: true },
    { key: "corpus_lasts",       label: "Corpus Sustains Full Duration", format: (v) => (v ? "Yes" : "No"), onlyIfValue: true },
    { key: "current_age",        label: "Current Age",                                   onlyIfValue: true },
    { key: "retirement_age",     label: "Retirement Age",                                onlyIfValue: true },
    { key: "monthly_expenses",   label: "Monthly Expenses",   format: fmt,               onlyIfValue: true },
    { key: "existing_corpus",    label: "Existing Corpus",    format: fmt,               onlyIfValue: true },
    { key: "annual_roi",         label: "Annual ROI (%)",                                onlyIfValue: true },
    { key: "years_to_retirement", label: "Years to Retirement",                          onlyIfValue: true },
    { key: "future_monthly_exp", label: "Inflation-Adjusted Monthly Expenses", format: fmt, onlyIfValue: true },
    { key: "future_existing",    label: "Future Value of Existing Corpus", format: fmt,  onlyIfValue: true },
    { key: "additional_needed",  label: "Additional Corpus Needed", format: fmt,         onlyIfValue: true },
    { key: "required_corpus",    label: "Required Corpus",    format: fmt,               onlyIfValue: true },
    { key: "monthly_sip_needed", label: "Monthly SIP Needed", format: fmt,               onlyIfValue: true },
    { key: "risk_score",         label: "Risk Score",                                    onlyIfValue: true },
    { key: "risk_profile",       label: "Risk Profile",                                  onlyIfValue: true },
    { key: "recommendation",     label: "Recommended Funds",                             onlyIfValue: true },
    { key: "risk_q_goal",        label: "What is your primary goal for this investment?",                    onlyIfValue: true },
    { key: "risk_q_horizon",     label: "What is your investment time horizon?",                              onlyIfValue: true },
    { key: "risk_q_reaction",    label: "If your portfolio dropped 20% in one month, what would you do?",     onlyIfValue: true },
    { key: "risk_q_knowledge",   label: "What is your current knowledge of financial markets?",                onlyIfValue: true },
    { key: "risk_q_employment",  label: "What is your current employment/income situation?",                  onlyIfValue: true },
  ],
};

const INTEREST_SECTION_TITLE = {
  "New Investment":       "Investment Details",
  "Goal Planning":        "Goal Planning Details",
  "New Product Offering": "Product Enquiry Details",
  "Wealth Projection":    "Wealth Projection Details",
  "Review My Investments": "Investment Details",
  "Financial Calculators": "Calculator Results",
  "Product Offerings":     "Product Enquiry Details",
  "One-Time Investment":   "Investment Details",
  "New SIP":               "SIP Interest Details",
  "Meeting / Conversation": "Meeting Request Details",
};

const normalizeRegisteredLeadDetail = (l) => {
  const d = l.interest_data || {};
  return {
    ...l,
    client_name:             l.name    ?? l.client_name,
    phone_number:            l.phone_no ?? l.phone_number,
    investment_amount:       d.investment_amount,
    fund_type:               d.fund_type,
    investment_period:       d.investment_period,
    goal_name:               d.goal,
    target_amount:           d.target_amount,
    years:                   d.timeline ?? d.years,
    sip_needed:              d.monthly_sip,
    lumpsum_needed:          d.lumpsum_today,
    product_category:        d.product_category,
    historical_returns:      d.historical_returns,
    product_interest_status: d.interest_status,
    // Wealth Projection
    current_value:           d.current_value,
    monthly_sip:             d.monthly_sip,
    annual_rate:             d.annual_rate,
    projected_wealth:        d.projected_wealth,
    // Financial Calculators
    calculator_type:         d.calculator_type,
    monthly_amount:          d.monthly_amount,
    invested:                d.invested,
    returns:                 d.returns,
    projected_corpus:        d.projected_corpus,
    principal:               d.principal,
    monthly_withdrawal:      d.monthly_withdrawal,
    total_withdrawn:         d.total_withdrawn,
    remaining_corpus:        d.remaining_corpus,
    corpus_lasts:            d.corpus_lasts,
    current_age:             d.current_age,
    retirement_age:          d.retirement_age,
    monthly_expenses:        d.monthly_expenses,
    existing_corpus:         d.existing_corpus,
    annual_roi:              d.annual_roi,
    years_to_retirement:     d.years_to_retirement,
    future_monthly_exp:      d.future_monthly_exp,
    future_existing:         d.future_existing,
    additional_needed:       d.additional_needed,
    required_corpus:         d.required_corpus,
    monthly_sip_needed:      d.monthly_sip_needed,
    risk_score:              d.risk_score,
    risk_profile:            d.risk_profile,
    recommendation:          d.recommendation,
    risk_q_goal:             d["What is your primary goal for this investment?"],
    risk_q_horizon:          d["What is your investment time horizon?"],
    risk_q_reaction:         d["If your portfolio dropped 20% in one month, what would you do?"],
    risk_q_knowledge:        d["What is your current knowledge of financial markets?"],
    risk_q_employment:       d["What is your current employment/income situation?"],
  };
};

const normalizeLead = (l) => {
  const d = l.interest_data || {};
  return {
    ...l,
    client_name:        l.name    ?? l.client_name,
    phone_number:       l.phone_no ?? l.phone_number,
    calculator_type:     d.calculator_type,
    monthly_amount:      d.monthly_amount,
    annual_rate:         d.annual_rate,
    years:               d.years,
    invested:            d.invested,
    returns:             d.returns,
    projected_corpus:    d.projected_corpus,
    principal:           d.principal,
    monthly_withdrawal:  d.monthly_withdrawal,
    total_withdrawn:     d.total_withdrawn,
    remaining_corpus:    d.remaining_corpus,
    corpus_lasts:        d.corpus_lasts,
    current_age:         d.current_age,
    retirement_age:      d.retirement_age,
    monthly_expenses:    d.monthly_expenses,
    existing_corpus:     d.existing_corpus,
    annual_roi:          d.annual_roi,
    years_to_retirement: d.years_to_retirement,
    future_monthly_exp:  d.future_monthly_exp,
    future_existing:     d.future_existing,
    additional_needed:   d.additional_needed,
    required_corpus:     d.required_corpus,
    monthly_sip_needed:  d.monthly_sip_needed,
    risk_score:          d.risk_score,
    risk_profile:        d.risk_profile,
    recommendation:      d.recommendation,
    risk_q_goal:         d["What is your primary goal for this investment?"],
    risk_q_horizon:      d["What is your investment time horizon?"],
    risk_q_reaction:     d["If your portfolio dropped 20% in one month, what would you do?"],
    risk_q_knowledge:    d["What is your current knowledge of financial markets?"],
    risk_q_employment:   d["What is your current employment/income situation?"],
    investment_value:    d.investment_value
  };
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Field = ({ label, value, isStatus, isProductStatus }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    {isStatus ? (
      <Box sx={{ mt: 0.5 }}>
        <Chip
          variant="outlined"
          size="small"
          label={value}
          color={STATUS_CHIP_COLOR[value] ?? "default"}
        />
      </Box>
    ) : isProductStatus ? (
      <Box sx={{ mt: 0.5 }}>
        <Chip
          variant="outlined"
          size="small"
          label={value}
          color={PRODUCT_INTEREST_COLOR[value] ?? "default"}
        />
      </Box>
    ) : (
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {value ?? "—"}
      </Typography>
    )}
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const LeadDetailView = ({ lead: initialLead, isRegistered, onBack }) => {
  const { getValidToken } = useAuth();
  const [lead, setLead] = useState(initialLead);

  const getHeaders = useCallback(async () => {
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  useEffect(() => {
    const fetchDetail = async () => {
      const endpoint = isRegistered ? MFD_REGISTERED_LEADS_ENDPOINT : MFD_LEADS_ENDPOINT;
      try {
        const headers = await getHeaders();
        const res = await fetch(`${endpoint}${initialLead.id}/`, { headers });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setLead(isRegistered ? normalizeRegisteredLeadDetail(data) : normalizeLead(data));
      } catch {
        // keep initial data on error
      }
    };
    fetchDetail();
  }, [initialLead.id, isRegistered, getHeaders]);

  const rawExtraFields = INTEREST_EXTRA_FIELDS[lead.interest] ?? [];
  const extraFields  = rawExtraFields.filter(({ key, onlyIfValue }) => !onlyIfValue || lead[key] != null);
  const sectionTitle = INTEREST_SECTION_TITLE[lead.interest];
  const hasBotData   = extraFields.some(({ key }) => lead[key] != null);

  return (
    <Box sx={{ p: 3 }}>
      <BackButton onClick={onBack} />

      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {isRegistered ? "Registered Lead Details" : "Lead Details"}
        </Typography>
        <Typography sx={{ ...typography.bodyLarge, color: "text.secondary", mt: 0.5 }}>
          {lead.client_name}
        </Typography>
      </Box>

      {/* Basic info */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          {isRegistered && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <Field label="PAN" value={lead.pan} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Field label="Client Code" value={lead.client_code} />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Name" value={lead.client_name} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Phone Number" value={lead.phone_number} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Email" value={lead.email} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Interest" value={lead.interest} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Status" value={lead.status} isStatus />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Comment" value={lead.comment} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Additional Details" value={lead.additional_details} />
          </Grid>
        </Grid>
      </Paper>

      {/* Bot-captured fields for this submission */}
      {hasBotData && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            {sectionTitle}
          </Typography>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Grid container spacing={3}>
              {extraFields.map(({ key, label, format, isProductStatus }) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Field
                    label={label}
                    value={format ? format(lead[key]) : lead[key]}
                    isProductStatus={isProductStatus}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default LeadDetailView;
